import { useMutation } from '@apollo/client/react';
import { datadogRum } from '@datadog/browser-rum';
import { GenerateSignedUrlDocument, Prefix } from '../graphql/types';
import { getImageData } from './getImageData';

const cleanFilename = (filename: string) => {
  const lastPeriodIndex = filename.lastIndexOf('.');
  if (lastPeriodIndex === -1) {
    return filename;
  }
  const filenameWithoutExtension = filename
    .slice(0, lastPeriodIndex)
    .replaceAll('.', '-');
  const extension = filename.slice(lastPeriodIndex + 1);

  return `${filenameWithoutExtension}.${extension}`;
};

export const fileErrors = {
  GENERIC_ERROR: 'Failed to upload image. Please try again later',
  TOO_BIG_IMAGE: 'The maximum file size is 5mb.',
  TOO_BIG_GENERAL: 'The maximum file size is 10mb.',
  UNSUPPORTED_FILE_TYPE: 'This file is not supported. Try a jpg png, or pdf.',
  INVALID_FILENAME_TOO_MANY_PERIODS:
    'This file name is invalid. It can only contain one period.',
};

export const isImageFileType = (fileType: string) =>
  fileType.match(/jpeg|jpg|png/gi);

function isFileTooLarge(file: File) {
  if (isImageFileType(file.type) && file.size / (1024 * 1024) > 5) {
    return fileErrors.TOO_BIG_IMAGE;
  }
  if (file.size / (1024 * 1024) > 10) {
    return fileErrors.TOO_BIG_GENERAL;
  }
  return false;
}

export function validateFile(file: File) {
  const response = {
    isValid: false,
    message: '',
  };
  // check type
  if (!file.type.match(/jpeg|png|jpg|pdf/gi)) {
    response.message = fileErrors.UNSUPPORTED_FILE_TYPE;
    return response;
  }
  // check size
  const fileIsTooLarge = isFileTooLarge(file);
  if (fileIsTooLarge) {
    response.message = fileIsTooLarge;
    return response;
  }

  response.isValid = true;
  return response;
}

export function useUploadFile() {
  const [generateSignedUrl] = useMutation(GenerateSignedUrlDocument);

  async function uploadFile(
    file: File,
    prefix: Prefix = Prefix.Uploads,
  ): Promise<{ url: string; key: string }> {
    const { width, height } = await getImageData(file);
    return new Promise((resolve, reject) => {
      try {
        if (!file) {
          reject(new Error('You must have a file to upload'));
          return;
        }
        const { isValid, message } = validateFile(file);
        if (!isValid) {
          reject(new Error(message));
          return;
        }

        // need to generate URL where we will upload file to
        generateSignedUrl({
          variables: {
            input: {
              prefix,
              contentType: file.type,
            },
          },
        })
          .then(async (result) => {
            if (!result?.data) {
              reject(new Error(fileErrors.GENERIC_ERROR));
              return;
            }

            const { signedUrl, photo, key } = result.data.generateSignedUrl;

            // finally upload image
            await fetch(signedUrl, {
              method: 'PUT',
              headers: {
                'Content-Type': file.type,
              },
              body: file,
            }).catch((error) => {
              datadogRum.addError(error);
              reject(new Error(fileErrors.GENERIC_ERROR));
            });

            // Build URL for image that was just uploaded
            const url = isImageFileType(file.type)
              ? `${photo.basePath}/${
                  photo.id
                }/${width}x${height}.${file.type.replace('image/', '')}`
              : `${photo.basePath}/${photo.id}/${encodeURIComponent(cleanFilename(file.name))}`;

            resolve({ url, key });
          })
          .catch((error) => {
            if (error.message.includes('Invalid file type')) {
              reject(new Error(fileErrors.UNSUPPORTED_FILE_TYPE));
            }
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  return {
    uploadFile,
    isFileTooLarge,
    getImageData,
  };
}
